<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  TransitionRoot, TransitionChild,
  Dialog, DialogPanel, DialogTitle,
} from '@headlessui/vue'
import { useFeedback } from '@/composables/useFeedback'

const {
  modalOpen, sentiment, submitting, submitError,
  closeFeedback, submitFeedback,
} = useFeedback()

const comment = ref('')

// Reset the textarea each time the modal opens for a new response.
watch(modalOpen, (open) => {
  if (open) comment.value = ''
})

const isDown = computed(() => sentiment.value === 'down')
const title = computed(() => (isDown.value ? 'What went wrong?' : 'Share more feedback'))
const promptText = computed(() =>
  isDown.value
    ? 'Sorry this response missed the mark. Telling us what was wrong helps us improve.'
    : 'Glad this was helpful! Add any details you would like to share (optional).',
)
const placeholder = computed(() =>
  isDown.value
    ? 'e.g. inaccurate, missing sources, wrong region/date, hard to read...'
    : 'e.g. what was especially useful...',
)

function onSubmit() {
  submitFeedback(comment.value)
}
</script>

<template>
  <TransitionRoot :show="modalOpen" as="template">
    <Dialog class="relative z-50" @close="closeFeedback">
      <TransitionChild
        as="template"
        enter="duration-150 ease-out" enter-from="opacity-0" enter-to="opacity-100"
        leave="duration-100 ease-in" leave-from="opacity-100" leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 flex items-center justify-center p-4">
        <TransitionChild
          as="template"
          enter="duration-150 ease-out" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100"
          leave="duration-100 ease-in" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95"
        >
          <DialogPanel class="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <div class="flex items-start gap-3">
              <div
                class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                :class="isDown ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'"
              >
                <svg v-if="isDown" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M14 11V4h2.5A1.5 1.5 0 0118 5.5v4A1.5 1.5 0 0116.5 11H14zm0 0l-3.2 6.2a1.5 1.5 0 01-2.8-.7V14H4.6a1.5 1.5 0 01-1.48-1.76l1-5.5A1.5 1.5 0 015.1 4H14" />
                </svg>
                <svg v-else class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6 9v7H3.5A1.5 1.5 0 012 14.5v-4A1.5 1.5 0 013.5 9H6zm0 0l3.2-6.2a1.5 1.5 0 012.8.7V6h3.4a1.5 1.5 0 011.48 1.76l-1 5.5A1.5 1.5 0 0114.9 16H6" />
                </svg>
              </div>
              <div class="flex-1">
                <DialogTitle class="text-sm font-semibold text-neutral-800">{{ title }}</DialogTitle>
                <p class="mt-1 text-xs text-neutral-500">{{ promptText }}</p>
              </div>
            </div>

            <textarea
              v-model="comment"
              rows="4"
              :placeholder="placeholder"
              class="mt-3 w-full resize-none rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-700 placeholder:text-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

            <p v-if="submitError" class="mt-2 text-xs text-red-600">{{ submitError }}</p>

            <div class="mt-4 flex justify-end gap-2">
              <button
                type="button"
                class="rounded-lg px-3 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100"
                :disabled="submitting"
                @click="closeFeedback"
              >
                Cancel
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                :disabled="submitting"
                @click="onSubmit"
              >
                <svg v-if="submitting" class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                {{ submitting ? 'Submitting...' : 'Submit feedback' }}
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
